import React from 'react';
import { Outlet, NavLink, useParams, useNavigate, useOutletContext } from 'react-router-dom';
import {
    ArrowLeft, Loader2, AlertCircle, ArrowRight, XCircle, Briefcase, SkipForward,
    FileText, Utensils, History, Users, ClipboardList, Phone, Mail, UserRound,
} from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { Button } from '../ui/Button';
import { PIPELINE_STAGE_LABELS, PipelineStage } from './pipelineStages';
import { getNextTransition, getSkipTransition, getAbandonTransition, PipelineTransition } from './pipelineTransitions';
import { PipelineActionModal } from './PipelineActionModal';
import { CreerPrivatisationModal } from './CreerPrivatisationModal';
import { FicheDocument } from './PipelineDocuments';

/**
 * LE DOSSIER, PAS LA PRIVATISATION. Ce qui est identifié ici est `dossier_id` : la privatisation
 * (`event_id`) n'existe qu'à partir de la signature du devis, et vaut `null` avant. Tout écran qui affiche
 * une date, un nombre de couverts ou un lien vers Événementiel doit donc supporter son absence.
 */
export interface Dossier {
    dossier_id: string;
    label: string;
    type: string;
    first_name: string;
    last_name: string;
    company_name: string;
    organizer_name: string;
    organizer_email: string;
    organizer_phone: string;
    email: string;
    phone: string;
    stage: PipelineStage;
    stage_since?: string | null;
    formule_retenue: string;
    devis_amount_ttc?: number | null;
    plaquette_sent_at?: string | null;
    client_reply_at?: string | null;
    brief_collected_at?: string | null;
    devis_sent_at?: string | null;
    devis_signed_at?: string | null;
    /** Le lien public du devis : à qui il est parti. */
    devis_sent_to?: string;
    /** Le client a DÉPOSÉ son exemplaire signé. Distinct de devis_signed_at, que seule la validation
     *  du commercial pose : recevoir n'est pas valider. */
    devis_signed_received_at?: string | null;
    devis_signed_document_id?: string | null;
    event_created_at?: string | null;
    prestataires_at?: string | null;
    fournisseurs_at?: string | null;
    menus_started_at?: string | null;
    jour_j_at?: string | null;
    closed_at?: string | null;
    lost_at?: string | null;
    lost_reason: string;
    documents: FicheDocument[];
    /** La privatisation dans Événementiel — null tant que le devis n'est pas signé. */
    event?: { id: string; start_time: string; end_time: string; num_people?: number | null } | null;
    collecte_status?: string | null;
    rendez_vous?: { id: string; subject: string; appointment_date: string; start_time: string; status: string } | null;
    /** Ce qu'est devenue la plaquette envoyée : le client a-t-il suivi le lien et répondu ? */
    plaquette_response?: {
        request_id: string;
        template_name: string;
        offered_appointment: boolean;
        responded: boolean;
        responded_at: string | null;
        appointment: { id: string; appointment_date: string; start_time: string } | null;
    } | null;
    journal: { from_stage: string | null; to_stage: string; actor_label: string; note: string; changed_at: string }[];
}

interface DossierContext {
    dossier: Dossier;
    rafraichir: () => void;
}

/**
 * Contexte du dossier, à consommer depuis n'importe quel onglet. Transposé du `useProjectContext()` de
 * RyoFlow : le layout charge la fiche UNE fois et la partage, au lieu que chaque onglet la recharge en
 * arrivant. Changer d'onglet devient instantané, et tous les onglets voient le même état.
 */
// eslint-disable-next-line react-refresh/only-export-components -- hook colocalisé avec son layout, comme chez RyoFlow
export function useDossierContext() {
    return useOutletContext<DossierContext>();
}

const ONGLETS: { id: string; label: string; icon: React.ComponentType<{ size?: number }> }[] = [
    { id: 'pipeline', label: 'Pipeline', icon: Briefcase },
    { id: 'brief', label: 'Brief', icon: ClipboardList },
    { id: 'documents', label: 'Documents', icon: FileText },
    { id: 'menus', label: 'Menus & Jour J', icon: Utensils },
    { id: 'journal', label: 'Historique', icon: History },
];

const fmtDate = (d?: string | null) => {
    if (!d) return '—';
    try { return new Date(d).toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' }); } catch { return '—'; }
};

/**
 * Un dossier = une page à onglets, structure reprise de `ProjectWorkspaceLayout.tsx` (RyoFlow), à la
 * demande d'Emeni — plutôt que de réinventer une mise en page maison.
 *
 * Ce qui est repris, et pourquoi ça vaut mieux qu'un onglet piloté par un état local :
 *   — chaque onglet est une VRAIE sous-route, donc l'adresse dit où l'on est. On peut envoyer un lien
 *     direct vers les documents d'un dossier, et le bouton « précédent » du navigateur fait ce qu'on attend.
 *   — la fiche est chargée une seule fois par le layout et partagée par `Outlet context`, avec une fonction
 *     de rafraîchissement commune : aucun onglet ne recharge en arrivant.
 *   — la liste des onglets est une donnée, pas du JSX recopié : en ajouter un tient en une ligne.
 *
 * Ce qui n'est PAS repris : leur habillage (couleurs codées en dur par onglet, variables CSS maison). Les
 * couleurs de L'IAmani sont centralisées, et le cadre interdit d'écrire une couleur en dur dans un écran.
 */
export function SuiviPrivatisationLayout() {
    const { dossierId } = useParams<{ dossierId: string }>();
    const navigate = useNavigate();
    const [dossier, setDossier] = React.useState<Dossier | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [erreur, setErreur] = React.useState('');

    const [action, setAction] = React.useState<PipelineTransition | null>(null);
    const [saving, setSaving] = React.useState(false);
    const [erreurAction, setErreurAction] = React.useState('');

    const charger = React.useCallback(() => {
        if (!dossierId) return;
        moduleApi.getDossierPrivatisation(dossierId)
            .then((r: any) => { setDossier(r); setErreur(''); })
            .catch((e: any) => setErreur(e?.message || 'Dossier introuvable.'))
            .finally(() => setLoading(false));
    }, [dossierId]);
    React.useEffect(() => { charger(); }, [charger]);

    const appliquer = async (payload: Record<string, any>) => {
        if (!dossierId) return;
        setSaving(true);
        setErreurAction('');
        try {
            await moduleApi.transitionDossierPrivatisation(dossierId, payload);
            setAction(null);
            charger();
        } catch (e: any) {
            setErreurAction(e?.message || "L'étape n'a pas pu être enregistrée.");
        } finally {
            setSaving(false);
        }
    };

    const creerPrivatisation = async (payload: Record<string, any>) => {
        if (!dossierId) return;
        setSaving(true);
        setErreurAction('');
        try {
            await moduleApi.createEventFromDossier(dossierId, payload);
            setAction(null);
            charger();
        } catch (e: any) {
            setErreurAction(e?.message || "La privatisation n'a pas pu être créée.");
        } finally {
            setSaving(false);
        }
    };

    if (loading) {
        return (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
                <Loader2 className="animate-spin text-content-muted" size={24} />
                <span className="text-xs text-content-muted">Chargement du dossier...</span>
            </div>
        );
    }

    if (erreur || !dossier || !dossierId) {
        return (
            <div className="flex flex-col items-center justify-center py-16 gap-3">
                <AlertCircle className="text-red-400" size={26} />
                <span className="text-sm font-semibold text-content">{erreur || 'Dossier introuvable'}</span>
                <Button variant="outline" size="sm" onClick={() => navigate('/suivi-privatisation')}>
                    <ArrowLeft size={13} /> Retour au suivi
                </Button>
            </div>
        );
    }

    const suivante = getNextTransition(dossier.stage, { hasSignedDevis: !!dossier.devis_signed_received_at });
    const passer = getSkipTransition(dossier.stage);
    const abandon = getAbandonTransition(dossier.stage);
    const lancer = (t: PipelineTransition) => { setErreurAction(''); setAction(t); };

    return (
        <div className="space-y-5">
            <div>
                <button onClick={() => navigate('/suivi-privatisation')} className="flex items-center gap-1.5 text-xs font-bold text-content-muted hover:text-content mb-3">
                    <ArrowLeft size={14} /> Retour au suivi
                </button>
                <div className="grid grid-cols-1 lg:grid-cols-[1fr,auto] gap-3 items-start">
                    <div>
                        <h1 className="text-xl font-bold text-content tracking-tight">{dossier.label}</h1>
                        <p className="text-content-muted text-sm mt-0.5 flex items-center gap-2 flex-wrap">
                            {/* Avant la signature, il n'y a ni date ni couverts : on montre alors de quoi
                                rappeler le client, qui est la seule action utile à ce stade. */}
                            {dossier.event ? (
                                <>
                                    <span>{fmtDate(dossier.event.start_time)}</span>
                                    {dossier.event.num_people ? <span className="flex items-center gap-1"><Users size={12} /> {dossier.event.num_people}</span> : null}
                                </>
                            ) : (
                                <>
                                    {dossier.phone && <span className="flex items-center gap-1"><Phone size={12} /> {dossier.phone}</span>}
                                    {dossier.email && <span className="flex items-center gap-1"><Mail size={12} /> {dossier.email}</span>}
                                </>
                            )}
                            {/* Le référent reste affiché même une fois la date connue : c'est LUI qu'on
                                rappelle, pas le standard de la société (demande d'Emeni, 2026-08-30). */}
                            {(dossier.organizer_name || dossier.organizer_email || dossier.organizer_phone) && (
                                <span className="flex items-center gap-1 pl-2 border-l border-border" title="Référent / organisateur">
                                    <UserRound size={12} />
                                    {[dossier.organizer_name, dossier.organizer_phone, dossier.organizer_email].filter(Boolean).join(' · ')}
                                </span>
                            )}
                            <span className="text-[10px] font-black uppercase px-2 py-0.5 rounded bg-brand-500/10 text-brand-600 dark:text-brand-400 border border-brand-500/20">
                                {PIPELINE_STAGE_LABELS[dossier.stage]}
                            </span>
                        </p>
                    </div>
                    {/* Les actions vivent dans l'en-tête du layout, pas dans un onglet : faire avancer un
                        dossier doit rester possible d'où qu'on le regarde. Même emplacement que le menu de
                        statut chez RyoFlow. */}
                    <div className="flex items-center gap-2 flex-wrap">
                        {suivante && (
                            <Button variant="primary" size="sm" onClick={() => lancer(suivante)}>
                                {suivante.actionLabel} <ArrowRight size={12} />
                            </Button>
                        )}
                        {passer && (
                            <Button variant="ghost" size="sm" onClick={() => lancer(passer)}>
                                <SkipForward size={12} /> Passer
                            </Button>
                        )}
                        {abandon && (
                            <Button variant="ghost" size="sm" onClick={() => lancer(abandon)}>
                                <XCircle size={12} /> {abandon.actionLabel}
                            </Button>
                        )}
                    </div>
                </div>
            </div>

            <div className="flex items-end gap-1 border-b border-border overflow-x-auto">
                {ONGLETS.map((o) => (
                    <NavLink key={o.id} to={o.id}
                        className={({ isActive }) => `flex items-center gap-1.5 px-3 py-2 text-xs font-bold whitespace-nowrap border-b-2 -mb-px transition-colors ${
                            isActive ? 'border-brand-600 text-content' : 'border-transparent text-content-muted hover:text-content'}`}>
                        <o.icon size={14} /> {o.label}
                    </NavLink>
                ))}
            </div>

            <Outlet context={{ dossier, rafraichir: charger } satisfies DossierContext} />

            {action && action.opensForm === 'creer_privatisation' ? (
                <CreerPrivatisationModal
                    label={dossier.label}
                    saving={saving}
                    error={erreurAction}
                    onCancel={() => setAction(null)}
                    onConfirm={creerPrivatisation}
                />
            ) : action ? (
                <PipelineActionModal
                    label={dossier.label}
                    transition={action}
                    destinataire={dossier.organizer_email || dossier.email}
                    destinataireEstReferent={!!dossier.organizer_email}
                    saving={saving}
                    error={erreurAction}
                    onCancel={() => setAction(null)}
                    onConfirm={appliquer}
                />
            ) : null}
        </div>
    );
}
